import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';

import { AccountActions } from '@lockton-apps/auth/action';
import { UsersTransformationsService } from '@lockton-apps/client-gateway/transformations';
import { AuthApiActions } from '@lockton-apps/shared/action/auth';
import { ContactsApiActions } from '@lockton-apps/shared/action/contacts';
import { UsersApiActions } from '@lockton-apps/shared/action/users';
import { ContactsService } from '@lockton-apps/shared/data-access/contacts';
import { UsersService } from '@lockton-apps/shared/data-access/users';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';
import { isBrokerOrHigher, isContact } from '@lockton-apps/shared/util/helpers';
import { LocalizationService } from '@lockton-apps/shared/util/localization';

import {
  selectAuthenticatedContact,
  selectAuthenticatedUser,
  selectAuthenticatedUserRole,
} from './auth-user.selectors';

@Injectable()
export class AuthUsersEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);

  updateAuthenticatedUserLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.languageUpdated),
      concatLatestFrom(() => [this.store.select(selectAuthenticatedUser)]),
      filter(([, authenticatedUser]) =>
        authenticatedUser ? isBrokerOrHigher(authenticatedUser.role) : false
      ),
      switchMap(([{ locale }, authenticatedUser]) => {
        if (!authenticatedUser) {
          throw new Error('Authenticated user is null');
        }
        const userUpdate =
          this.transformService.userToUserUpdate(authenticatedUser);
        return this.usersService
          .update({
            ...userUpdate,
            language: locale,
          })
          .pipe(
            map((res) =>
              UsersApiActions.updateLanguageSuccess({
                user: res,
              })
            )
          );
      })
    )
  );

  updateLanguageSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          UsersApiActions.updateLanguageSuccess,
          ContactsApiActions.updateLanguageSuccess
        ),
        tap(() => {
          this.feedbackService.subtleMessage(
            $localize`Language updated successfully`,
            {
              type: 'success',
            }
          );
        })
      ),
    { dispatch: false }
  );

  switchLocaleForUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersApiActions.updateLanguageSuccess),
        tap(({ user }) => {
          this.localizationService.switchLocale(user.language);
        })
      ),
    { dispatch: false }
  );

  loadAuthenticatedContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.contactAuthenticated),
      switchMap(() => {
        return this.contactsService.loadForAuthenticatedUser().pipe(
          map((contact) =>
            ContactsApiActions.loadAuthenticatedContactSuccess({ contact })
          ),
          catchError((error) =>
            of(ContactsApiActions.loadAuthenticatedContactFailure({ error }))
          )
        );
      })
    )
  );

  updateAuthenticatedContactLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.languageUpdated),
      concatLatestFrom(() => [
        this.store.select(selectAuthenticatedContact),
        this.store.select(selectAuthenticatedUserRole),
      ]),
      filter(([, , authenticatedUserRole]) =>
        authenticatedUserRole ? isContact(authenticatedUserRole) : false
      ),
      switchMap(([{ locale }, authenticatedContact]) => {
        if (!authenticatedContact) {
          throw new Error('Authenticated contact is null');
        }
        return this.contactsService
          .update(
            { ...authenticatedContact, language: locale },
            authenticatedContact.userId
          )
          .pipe(
            map((contact) =>
              ContactsApiActions.updateLanguageSuccess({ contact })
            ),
            catchError((error) =>
              of(ContactsApiActions.updateLanguageFailure({ error }))
            )
          );
      })
    )
  );

  switchLocaleForContact$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ContactsApiActions.updateLanguageSuccess),
        tap(({ contact }) => {
          this.localizationService.switchLocale(contact.language);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private usersService: UsersService,
    private contactsService: ContactsService,
    private transformService: UsersTransformationsService,
    private feedbackService: FeedbackService,
    private localizationService: LocalizationService
  ) {}
}
